/**
 * Precious Metals Charts Hub - Live Data Integration
 * ab-charts.js - WITH CACHING
 */

(function() {
  'use strict';

  // Constants
  const TROY_OZ_TO_GRAM = 31.1034768;
  const TROY_OZ_TO_KILO = 0.0311034768;
  const UPDATE_INTERVAL = 600000; // 10 minute for live prices
  const MAX_RETRIES = 3;
  const METALS = ['gold', 'silver', 'platinum', 'palladium'];
  const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];
  
  const CURRENCY_SYMBOLS = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: 'C$',
    AUD: 'A$',
    JPY: '¥'
  };

  const METAL_COLORS = {
    gold: '#FFD700',
    silver: '#C0C0C0',
    platinum: '#E5E4E2',
    palladium: '#676767'
  };

  const TIME_RANGES = {
    '1D': { days: 1, interval: 'hourly' },
    '1W': { days: 7, interval: 'daily' },
    '1M': { days: 30, interval: 'daily' },
    'YTD': { days: null, interval: 'daily' }, // Calculate dynamically
    
  };

  // State - UPDATED WITH CACHE
  let state = {
    prices: {},
    fx: {},
    history: {},
    historyCache: {  // NEW: Cache for historical data
      data: {},
      timestamp: null,
      duration: 3600000 // 1 hour cache
    },
    charts: {}, // Store Chart.js instances
    selectedUnit: 'oz',
    selectedCurrency: 'USD',
    chartScale: 'linear',
    normalize: false,
    selectedRange: '1M',
    retryCount: 0,
    updateTimer: null,
    lastUpdate: null,
    mobileMetal: 'gold'
  };

  // Utils
  function abFormatNumber(num, decimals = 2, currency = 'USD') {
    if (typeof num !== 'number' || isNaN(num)) return '--';
    if (currency === 'JPY') return Math.round(num).toLocaleString();
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function abToGrams(ozPrice) { return ozPrice / TROY_OZ_TO_GRAM; }
  function abToKilos(ozPrice) { return ozPrice / TROY_OZ_TO_KILO; }

  function abConvertCurrency(usdPrice, targetCurrency) {
    if (usdPrice == null) return null;
    if (targetCurrency === 'USD') return usdPrice;
    const direct = state.fx[`${targetCurrency}USD`];
    const inverse = state.fx[`USD${targetCurrency}`];
    if (typeof direct === 'number') return usdPrice / direct;
    if (typeof inverse === 'number') return usdPrice * inverse;
    return null;
  }

  function abNormalizeSeries(data, startIndex = 0) {
    if (!data || data.length === 0) return [];
    const baseValue = data[startIndex];
    return data.map(val => (val / baseValue) * 100);
  }

  function abGetPriceByUnit(metalPrice, unit) {
    if (typeof metalPrice !== 'number') return null;
    switch(unit) {
      case 'oz': return metalPrice;
      case 'g': return abToGrams(metalPrice);
      case 'kg': return abToKilos(metalPrice);
      default: return metalPrice;
    }
  }

  function getYTDDays() {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    return Math.floor((now - startOfYear) / (1000 * 60 * 60 * 24));
  }

  // API Functions
  async function fetchPrices() {
    const container = document.querySelector('.ab-charts-hub');
    if (!container) {
      console.warn('[AB Charts] .ab-charts-hub not found');
      return;
    }

    const apiUrl = String((container.dataset.apiUrl || window.AB_CHARTS_API_URL || '')).trim();
    if (!apiUrl) {
      console.warn('[AB Charts] No API URL configured');
      showFallbackMessage();
      return;
    }

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);

      const res = await fetch(apiUrl, {
        signal: controller.signal,
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      });

      clearTimeout(timeout);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      processPriceData(data);
      
      if (data.fx) {
        state.fx = data.fx;
      }

      state.retryCount = 0;
      updateAllComponents();
      
      // Fetch historical data for visible charts
      fetchHistoricalData();
      
    } catch (error) {
      console.error('[AB Charts] Price fetch error:', error);
      handleFetchError();
    }
  }

  // UPDATED: Cached historical data fetching
  async function fetchHistoricalData() {
    const container = document.querySelector('.ab-charts-hub');
    const apiUrl = String((container.dataset.apiUrl || '')).trim();
    if (!apiUrl) return;

    // Check if we have valid cached data
    const now = Date.now();
    const range = TIME_RANGES[state.selectedRange];
    const cacheKey = `${state.selectedRange}_${range.days || getYTDDays()}`;
    
    if (state.historyCache.timestamp && 
        (now - state.historyCache.timestamp) < state.historyCache.duration &&
        state.historyCache.data[cacheKey] &&
        Object.keys(state.historyCache.data[cacheKey]).length > 0) {
      console.log('[AB Charts] Using cached historical data');
      state.history = state.historyCache.data[cacheKey];
      updateCharts();
      return;
    }

    const days = range.days || getYTDDays();

    // Fetch all metals in parallel
    const fetchPromises = METALS.map(metal => {
      return fetch(`${apiUrl}?action=historical&metal=${metal}&days=${days}`, {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.data && Array.isArray(data.data)) {
          return {
            metal,
            data: data.data.map(point => ({
              time: new Date(point.date + 'T16:00:00Z'),
              price: point.close || point.price,
              open: point.open,
              high: point.high,
              low: point.low,
              close: point.close
            }))
          };
        }
        return null;
      })
      .catch(err => {
        console.warn(`[AB Charts] Failed to fetch ${metal}:`, err);
        return null;
      });
    });

    // Wait for all fetches to complete
    const results = await Promise.all(fetchPromises);
    
    // Process results
    const newHistory = {};
    results.forEach(result => {
      if (result) {
        newHistory[result.metal] = result.data;
      }
    });

    // Update cache if we got data
    if (Object.keys(newHistory).length > 0) {
      // Initialize cache data structure if needed
      if (!state.historyCache.data) {
        state.historyCache.data = {};
      }
      
      state.historyCache.data[cacheKey] = newHistory;
      state.historyCache.timestamp = now;
      state.history = newHistory;
      
      // Save to localStorage for persistence
      try {
        const cacheData = {
          data: state.historyCache.data,
          timestamp: now
        };
        localStorage.setItem('ab_charts_history_cache', JSON.stringify(cacheData));
        console.log('[AB Charts] Saved historical data to cache');
      } catch (e) {
        console.warn('[AB Charts] Could not save to localStorage:', e);
      }
    }

    updateCharts();
  }

  function processPriceData(data) {
    try {
      if (!data || typeof data !== 'object') throw new Error('No JSON');

      const metalsObj = data.metals || {};
      const fxObj = data.fx || {};

      const metalsMap = {
        gold: metalsObj.gold || data.XAUUSD || data.gold || data.XAU,
        silver: metalsObj.silver || data.XAGUSD || data.silver || data.XAG,
        platinum: metalsObj.platinum || data.XPTUSD || data.platinum || data.XPT,
        palladium: metalsObj.palladium || data.XPDUSD || data.palladium || data.XPD
      };

      if (fxObj && typeof fxObj === 'object') state.fx = fxObj;
      state.lastUpdate = data.timestamp ? new Date(data.timestamp) : new Date();

      Object.entries(metalsMap).forEach(([metal, m]) => {
        if (!m || (typeof m !== 'object' && typeof m !== 'number')) return;

        const mid = typeof m === 'number'
          ? m
          : (typeof m.mid === 'number'
            ? m.mid
            : (typeof m.bid === 'number' && typeof m.ask === 'number'
              ? (m.bid + m.ask) / 2
              : null));

        const bid = (typeof m === 'object' && typeof m.bid === 'number') ? m.bid : (mid != null ? mid - 0.5 : null);
        const ask = (typeof m === 'object' && typeof m.ask === 'number') ? m.ask : (mid != null ? mid + 0.5 : null);
        const change = (typeof m === 'object' && typeof m.change === 'number') ? m.change : 0;
        const changePct = (typeof m === 'object' && typeof m.changePct === 'number') ? m.changePct : 0;

        if (mid != null) {
          state.prices[metal] = { bid, ask, mid, change, changePct };
        }
      });
    } catch (err) {
      console.error('[AB Charts] processPriceData error:', err);
    }
  }

  function handleFetchError() {
    state.retryCount++;
    if (state.retryCount <= MAX_RETRIES) {
      const backoffTime = Math.min(UPDATE_INTERVAL * Math.pow(2, state.retryCount - 1), 300000);
      setTimeout(fetchPrices, backoffTime);
    } else {
      showFallbackMessage();
    }
  }

  function showFallbackMessage() {
    const elements = document.querySelectorAll('.ab-price-value, .ab-card-price, .ab-mini-price');
    elements.forEach(el => {
      if (el.textContent === '$--' || el.textContent === '--') {
        el.textContent = 'Unavailable';
        el.style.fontSize = '14px';
      }
    });
    const tickerTime = document.getElementById('ab-ticker-time');
    if (tickerTime) tickerTime.textContent = 'Data temporarily unavailable';
  }

  // Chart Functions
  function updateCharts() {
    renderMainComparisonChart();
    renderMiniCharts();
  }

  function renderMainComparisonChart() {
    const container = document.getElementById('ab-comparison-chart');
    if (!container) return;

    // Check which metals are enabled
    const enabledMetals = [];
    document.querySelectorAll('.ab-metal-toggles input:checked').forEach(cb => {
      const metal = cb.dataset.metal;
      if (state.history[metal] && state.history[metal].length > 0) {
        enabledMetals.push(metal);
      }
    });

    if (enabledMetals.length === 0) {
      container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #718096;">Select metals and wait for data to load</div>';
      return;
    }

    // Create or get canvas
    let canvas = container.querySelector('canvas');
    if (!canvas) {
      container.innerHTML = '';
      canvas = document.createElement('canvas');
      canvas.style.width = '100%';
      canvas.style.height = '400px';
      container.appendChild(canvas);
    }

    const ctx = canvas.getContext('2d');

    // Prepare datasets
    const datasets = enabledMetals.map(metal => {
      const data = state.history[metal];
      let chartData = data.map(point => ({
        x: point.time,
        y: point.price
      }));

      // Apply normalization if needed
      if (state.normalize && chartData.length > 0) {
        const baseValue = chartData[0].y;
        chartData = chartData.map(point => ({
          x: point.x,
          y: (point.y / baseValue) * 100
        }));
      }

      // Apply currency conversion
      if (state.selectedCurrency !== 'USD') {
        chartData = chartData.map(point => ({
          x: point.x,
          y: abConvertCurrency(point.y, state.selectedCurrency) || point.y
        }));
      }

      return {
        label: metal.charAt(0).toUpperCase() + metal.slice(1),
        data: chartData,
        borderColor: METAL_COLORS[metal],
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1
      };
    });

    // Destroy existing chart if exists
    if (state.charts.main) {
      state.charts.main.destroy();
    }

    // Create new chart
    state.charts.main = new Chart(ctx, {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                const symbol = CURRENCY_SYMBOLS[state.selectedCurrency] || '$';
                const formatted = state.normalize 
                  ? `${value.toFixed(2)}%`
                  : `${symbol}${abFormatNumber(value, 2, state.selectedCurrency)}`;
                return `${label}: ${formatted}`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              displayFormats: {
                hour: 'MMM dd HH:mm',
                day: 'MMM dd',
                month: 'MMM yyyy'
              }
            },
            grid: {
              display: true,
              color: 'rgba(0,0,0,0.05)'
            }
          },
          y: {
            type: state.chartScale,
            grid: {
              display: true,
              color: 'rgba(0,0,0,0.05)'
            },
            title: {
              display: true,
              text: state.normalize ? 'Indexed (100 = start)' : `Price (${state.selectedCurrency})`
            },
            ticks: {
              callback: function(value) {
                const symbol = CURRENCY_SYMBOLS[state.selectedCurrency] || '$';
                return state.normalize 
                  ? `${value.toFixed(0)}%`
                  : `${symbol}${abFormatNumber(value, 0, state.selectedCurrency)}`;
              }
            }
          }
        }
      }
    });
  }

  function renderMiniCharts() {
    METALS.forEach(metal => {
      const container = document.getElementById(`ab-${metal}-mini-chart`);
      if (!container || !state.history[metal] || state.history[metal].length < 2) return;

      let canvas = container.querySelector('canvas');
      if (!canvas) {
        container.innerHTML = '';
        canvas = document.createElement('canvas');
        canvas.style.width = '100%';
        canvas.style.height = '200px';
        container.appendChild(canvas);
      }

      const ctx = canvas.getContext('2d');
      const data = state.history[metal];
      
      // Get selected range for mini chart
      const miniButtons = container.closest('.ab-mini-section').querySelectorAll('.ab-mini-chart-controls button[aria-pressed="true"]');
      const miniRange = miniButtons.length > 0 ? miniButtons[0].dataset.range : '1M';
      
      // Filter data based on range
      let filteredData = data;
      if (miniRange === '1M') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        filteredData = data.filter(d => d.time >= thirtyDaysAgo);
      } else if (miniRange === '1Y') {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        filteredData = data.filter(d => d.time >= oneYearAgo);
      }

      const chartData = filteredData.map(point => ({
        x: point.time,
        y: point.price
      }));

      // Destroy existing chart if exists
      if (state.charts[`mini-${metal}`]) {
        state.charts[`mini-${metal}`].destroy();
      }

      // Determine if trend is up or down
      const isUp = chartData.length > 1 && chartData[chartData.length - 1].y >= chartData[0].y;

      // Create new mini chart
      state.charts[`mini-${metal}`] = new Chart(ctx, {
        type: 'line',
        data: {
          datasets: [{
            data: chartData,
            borderColor: isUp ? '#48bb78' : '#f56565',
            backgroundColor: isUp ? 'rgba(72, 187, 120, 0.1)' : 'rgba(245, 101, 101, 0.1)',
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.2,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              callbacks: {
                label: function(context) {
                  return `$${abFormatNumber(context.parsed.y, 2)}`;
                }
              }
            }
          },
          scales: {
            x: {
              type: 'time',
              display: false
            },
            y: {
              display: false
            }
          }
        }
      });
    });
  }

  // UPDATE COMPONENTS
  function updateAllComponents() {
    updateQuadTicker();
    updateNavigationCards();
    updateUnitsTable();
    updateMiniSections();
    updateMobilePill();
  }

  function updateQuadTicker() {
    const unit = state.selectedUnit;
    const cur = state.selectedCurrency;
    const symbol = CURRENCY_SYMBOLS[cur] || '$';

    METALS.forEach(metal => {
      const prices = state.prices[metal];
      if (!prices) return;

      const el = document.querySelector(`.ab-ticker-item[data-metal="${metal}"]`);
      if (!el) return;

      let bid = abGetPriceByUnit(prices.bid, unit);
      let ask = abGetPriceByUnit(prices.ask, unit);

      if (cur !== 'USD') {
        bid = abConvertCurrency(bid, cur);
        ask = abConvertCurrency(ask, cur);
      }

      const decimals = cur === 'JPY' ? 0 : 2;

      const bidEl = el.querySelector('[data-price-type="bid"]');
      const askEl = el.querySelector('[data-price-type="ask"]');
      if (bidEl && typeof bid === 'number') bidEl.textContent = symbol + abFormatNumber(bid, decimals, cur);
      if (askEl && typeof ask === 'number') askEl.textContent = symbol + abFormatNumber(ask, decimals, cur);

      const changeEl = el.querySelector('[data-change-type="amount"]');
      const percentEl = el.querySelector('[data-change-type="percent"]');
      const changeWrap = el.querySelector('.ab-ticker-change');

      if (changeEl && percentEl && changeWrap) {
        const ch = prices.change ?? 0;
        const pct = prices.changePct ?? 0;
        changeEl.textContent = (ch >= 0 ? '+' : '') + abFormatNumber(ch, 2);
        percentEl.textContent = '(' + (pct >= 0 ? '+' : '') + abFormatNumber(pct, 2) + '%)';
        changeWrap.dataset.direction = ch >= 0 ? 'up' : 'down';
      }
    });

    const timeEl = document.getElementById('ab-ticker-time');
    if (timeEl && state.lastUpdate) {
      const timeStr = state.lastUpdate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
      timeEl.textContent = timeStr;
    }
  }

  function updateNavigationCards() {
    METALS.forEach(metal => {
      const prices = state.prices[metal];
      if (!prices) return;

      const priceEl = document.querySelector(`[data-card-price="${metal}"]`);
      const changeEl = document.querySelector(`[data-card-change="${metal}"]`);

      if (priceEl) priceEl.textContent = CURRENCY_SYMBOLS.USD + abFormatNumber(prices.mid);
      
      if (changeEl) {
        const changeAmount = changeEl.querySelector('.ab-card-change-amount');
        const changePercent = changeEl.querySelector('.ab-card-change-percent');
        if (changeAmount) changeAmount.textContent = (prices.change >= 0 ? '+' : '') + abFormatNumber(prices.change);
        if (changePercent) changePercent.textContent = '(' + (prices.changePct >= 0 ? '+' : '') + abFormatNumber(prices.changePct) + '%)';
        changeEl.style.color = prices.change >= 0 ? 'var(--ab-success)' : 'var(--ab-danger)';
      }

      updateSparkline(metal);
    });
  }

  function updateSparkline(metal) {
    const container = document.querySelector(`[data-sparkline="${metal}"]`);
    if (!container) return;

    const svg = container.querySelector('svg');
    if (!svg || !state.history[metal] || state.history[metal].length < 2) return;

    const points = state.history[metal].slice(-20);
    const prices = points.map(p => p.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;
    const width = 120;
    const height = 40;
    const step = width / (points.length - 1);
    const isUp = prices[prices.length - 1] >= prices[0];

    const d = points.map((pt, i) => {
      const x = i * step;
      const y = height - ((pt.price - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    }).join(' ');

    let path = svg.querySelector('path');
    if (!path) {
      path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      svg.appendChild(path);
    }

    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', isUp ? '#48bb78' : '#f56565');
    path.setAttribute('stroke-width', '2');
  }

  function updateUnitsTable() {
    METALS.forEach(metal => {
      const prices = state.prices[metal];
      if (!prices) return;

      ['oz', 'g', 'kg'].forEach(unit => {
        const row = document.querySelector(`tr[data-metal="${metal}"][data-unit="${unit}"]`);
        if (!row) return;

        const basePrice = abGetPriceByUnit(prices.mid, unit);

        CURRENCIES.forEach(currency => {
          const cell = row.querySelector(`td[data-currency="${currency}"]`);
          if (!cell) return;

          let price = basePrice;
          if (currency !== 'USD') price = abConvertCurrency(basePrice, currency);

          if (price !== null) {
            const symbol = CURRENCY_SYMBOLS[currency];
            const decimals = currency === 'JPY' ? 0 : 2;
            cell.textContent = symbol + abFormatNumber(price, decimals, currency);
          }
        });
      });
    });
  }

  function updateMiniSections() {
    METALS.forEach(metal => {
      const prices = state.prices[metal];
      if (!prices) return;

      const priceEl = document.querySelector(`.ab-mini-price[data-price="${metal}"]`);
      const changeEl = document.querySelector(`.ab-mini-change[data-change="${metal}"]`);

      if (priceEl) priceEl.textContent = CURRENCY_SYMBOLS.USD + abFormatNumber(prices.mid);
      
      if (changeEl) {
        changeEl.textContent = (prices.change >= 0 ? '+' : '') +
          abFormatNumber(prices.change) + ' (' +
          (prices.changePct >= 0 ? '+' : '') +
          abFormatNumber(prices.changePct) + '%)';
        changeEl.style.color = prices.change >= 0 ? 'var(--ab-success)' : 'var(--ab-danger)';
      }
    });
  }

  function updateMobilePill() {
    const metal = state.mobileMetal;
    const prices = state.prices[metal];
    if (!prices) return;

    const priceEl = document.querySelector('[data-mobile-price="gold"]');
    if (priceEl) priceEl.textContent = CURRENCY_SYMBOLS.USD + abFormatNumber(prices.mid);
  }

  // Event Handlers
  function initEventHandlers() {
    // Unit toggle
    document.querySelectorAll('.ab-unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.ab-unit-btn').forEach(b => {
          b.classList.remove('ab-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('ab-active');
        btn.setAttribute('aria-pressed', 'true');
        state.selectedUnit = btn.dataset.unit;
        updateQuadTicker();
      });
    });

    // Time range selection - UPDATED to clear cache on range change
    document.querySelectorAll('.ab-range-tabs button').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.ab-range-tabs button').forEach(b => 
          b.setAttribute('aria-selected', 'false'));
        btn.setAttribute('aria-selected', 'true');
        state.selectedRange = btn.dataset.range;
        fetchHistoricalData(); // Fetch new data for selected range
      });
    });

    // Currency selection
    const currencySelect = document.querySelector('.ab-currency-select');
    if (currencySelect) {
      currencySelect.addEventListener('change', (e) => {
        state.selectedCurrency = e.target.value;
        localStorage.setItem('ab-currency', state.selectedCurrency);
        updateAllComponents();
        updateCharts();
      });
    }

    // Chart scale toggle
    const scaleBtn = document.querySelector('[data-chart-scale]');
    if (scaleBtn) {
      scaleBtn.addEventListener('click', () => {
        const isLinear = scaleBtn.dataset.chartScale === 'linear';
        scaleBtn.dataset.chartScale = isLinear ? 'log' : 'linear';
        scaleBtn.innerHTML = isLinear
          ? '<span class="ab-inactive">Linear</span>/<span>Log</span>'
          : '<span>Linear</span>/<span class="ab-inactive">Log</span>';
        state.chartScale = scaleBtn.dataset.chartScale;
        updateCharts();
      });
    }

    // Normalize toggle
    const normalizeBtn = document.querySelector('[data-normalize]');
    if (normalizeBtn) {
      normalizeBtn.addEventListener('click', () => {
        const isNormalized = normalizeBtn.dataset.normalize === 'true';
        normalizeBtn.dataset.normalize = (!isNormalized).toString();
        normalizeBtn.setAttribute('aria-pressed', (!isNormalized).toString());
        state.normalize = !isNormalized;
        updateCharts();
      });
    }

    // Metal toggles for comparison chart
    document.querySelectorAll('.ab-metal-toggles input').forEach(cb => {
      cb.addEventListener('change', () => {
        updateCharts();
      });
    });

    // Mini chart controls
    document.querySelectorAll('.ab-mini-chart-controls button').forEach(btn => {
      btn.addEventListener('click', () => {
        const container = btn.closest('.ab-mini-chart-controls');
        container.querySelectorAll('button').forEach(b => 
          b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        
        // Re-render the mini chart with new range
        const metal = btn.closest('.ab-mini-section').dataset.metal;
        if (metal) {
          renderMiniCharts();
        }
      });
    });

    // Mobile pill
    const pillMenu = document.querySelector('.ab-pill-menu');
    const pillDropdown = document.querySelector('.ab-pill-dropdown');
    
    if (pillMenu && pillDropdown) {
      pillMenu.addEventListener('click', () => {
        pillDropdown.classList.toggle('ab-show');
      });

      pillDropdown.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => {
          state.mobileMetal = btn.dataset.metal;
          const label = document.querySelector('.ab-pill-metal');
          if (label) label.textContent = btn.textContent;
          pillDropdown.classList.remove('ab-show');
          updateMobilePill();
        });
      });

      document.addEventListener('click', (e) => {
        if (!e.target.closest('.ab-mobile-pill')) {
          pillDropdown.classList.remove('ab-show');
        }
      });
    }
  }

  // Initialization - UPDATED with cache loading
  function init() {
    // Load cached historical data from localStorage if available
    try {
      const cached = localStorage.getItem('ab_charts_history_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        const age = Date.now() - parsed.timestamp;
        if (age < state.historyCache.duration) {
          state.historyCache.data = parsed.data;
          state.historyCache.timestamp = parsed.timestamp;
          // Load the current range's data if available
          const range = TIME_RANGES[state.selectedRange];
          const cacheKey = `${state.selectedRange}_${range.days || getYTDDays()}`;
          if (parsed.data[cacheKey]) {
            state.history = parsed.data[cacheKey];
            console.log('[AB Charts] Loaded historical data from localStorage cache');
          }
        }
      }
    } catch (e) {
      console.warn('[AB Charts] Could not load cache from localStorage:', e);
    }

    // Load saved currency preference
    const savedCurrency = localStorage.getItem('ab-currency');
    if (savedCurrency && CURRENCIES.includes(savedCurrency)) {
      state.selectedCurrency = savedCurrency;
      const select = document.querySelector('.ab-currency-select');
      if (select) select.value = savedCurrency;
    }

    // Initialize event handlers
    initEventHandlers();

    // Initial data fetch
    fetchPrices();

    // Set up periodic updates
    state.updateTimer = setInterval(fetchPrices, UPDATE_INTERVAL);

    // Handle visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearInterval(state.updateTimer);
      } else {
        fetchPrices();
        state.updateTimer = setInterval(fetchPrices, UPDATE_INTERVAL);
      }
    });

    // Clean up on unload
    window.addEventListener('beforeunload', () => {
      if (state.updateTimer) clearInterval(state.updateTimer);
    });
  }

  // Wait for DOM and Chart.js to be ready
  function waitForChartJS(callback) {
    if (typeof Chart !== 'undefined') {
      callback();
    } else {
      setTimeout(() => waitForChartJS(callback), 100);
    }
  }

  // Start the application
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      waitForChartJS(init);
    });
  } else {
    waitForChartJS(init);
  }

  // Export utilities
  window.abChartsUtils = {
    formatNumber: abFormatNumber,
    toGrams: abToGrams,
    toKilos: abToKilos,
    convertCurrency: abConvertCurrency,
    normalizeSeries: abNormalizeSeries,
    getState: () => state
  };

})();